import { error } from "@wevideo/platform/logger";

import { IInProductContent } from "../models/InProductContent";

export const IN_PRODUCT_CONTENT_STORAGE_KEY = "wv_in_product_content";
const getInProductContentStorageKey = (userId: number) =>
	`${IN_PRODUCT_CONTENT_STORAGE_KEY}:${userId}`;

const isInProductContent = (value: unknown): value is IInProductContent => {
	const data = (value as Partial<IInProductContent> | null)?.data;
	return typeof data === "object" && data !== null;
};

export const toInProductContentList = (response: unknown): IInProductContent[] =>
	(Array.isArray(response) ? response : [response]).filter(isInProductContent);

/**
 * Every snapshot this origin has ever written, the pre-user-scoping key
 * included. Keys are collected before any are removed, so the caller is not
 * walking a list that shifts underneath it.
 */
const inProductContentKeys = () =>
	Object.keys(localStorage).filter(
		key =>
			key === IN_PRODUCT_CONTENT_STORAGE_KEY || key.startsWith(`${IN_PRODUCT_CONTENT_STORAGE_KEY}:`)
	);

/**
 * Drops every snapshot that is not this user's. Signing out is a full
 * navigation to the backend logout endpoint, so writing is the moment this code
 * can always converge on "at most one user's content on this device" — see
 * `clearAllInProductContent` for the moment it is signed out.
 */
const pruneForeignInProductContent = (userId: number) => {
	const own = getInProductContentStorageKey(userId);
	inProductContentKeys()
		.filter(key => key !== own)
		.forEach(key => localStorage.removeItem(key));
};

export const storeInProductContent = (userId: number, entries: IInProductContent[]) => {
	try {
		pruneForeignInProductContent(userId);
		localStorage.setItem(getInProductContentStorageKey(userId), JSON.stringify(entries));
	} catch (err) {
		error("Failed to persist in-product content", err);
	}
};

export const readInProductContent = (userId: number): IInProductContent[] => {
	try {
		const raw = localStorage.getItem(getInProductContentStorageKey(userId));

		if (!raw) {
			return [];
		}

		const parsed: unknown = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed.filter(isInProductContent) : [];
	} catch (err) {
		error("Failed to read in-product content", err);
		return [];
	}
};

/**
 * Mirrors a reported state onto the stored snapshot. Without this the snapshot
 * keeps serving an entry at its pre-interaction state, so a dismissed banner
 * reappears on the next reload until the response replaces the snapshot.
 *
 * Returns the state it replaced, so a failed report can put it back.
 *
 * A rollback passes what it wrote as `expectedState`: anything else in the
 * snapshot means a later interaction has already replaced it, and restoring the
 * older state would undo that one too — a failed view report would take a
 * dismissal down with it.
 */
export const markInProductContentState = (
	userId: number,
	uniqueKey: string,
	state: number,
	expectedState?: number
): number | undefined => {
	const entries = readInProductContent(userId);
	const previous = entries.find(entry => entry.uniqueKey === uniqueKey)?.state;
	if (previous === undefined) {
		return undefined;
	}

	if (expectedState !== undefined && previous !== expectedState) {
		return undefined;
	}

	storeInProductContent(
		userId,
		entries.map(entry => (entry.uniqueKey === uniqueKey ? { ...entry, state } : entry))
	);

	return previous;
};

/**
 * Forgets the content of whoever was signed in. Called from the surfaces a
 * signed-out user lands on, because the logout endpoint is the backend's and
 * gives the client no teardown of its own.
 */
export const clearAllInProductContent = () => {
	try {
		inProductContentKeys().forEach(key => localStorage.removeItem(key));
	} catch (err) {
		error("Failed to clear in-product content", err);
	}
};
